// 👤 بوابة المواطن بصفحات منظمة - أريح للعين
import React, { useState, useEffect } from 'react';
import { 
  Bell, AlertTriangle, Shield, Lightbulb, User, MapPin, Phone,
  Home, MessageSquare, Heart, Clock, Bot
} from 'lucide-react';
import { useCitizen } from '../contexts/CitizenContext';
import SmartChatAgent from './SmartChatAgent';

const CitizenPortalPages = ({ 
  selectedCitizen, 
  citizenNotifications, 
  unreadCount,
  markAsRead 
}) => {
  // استخدام النظام المركزي
  const { 
    selectedCitizen: centralCitizen, 
    selectCitizen, 
    clearSelection,
    addInteraction, 
    setActivePage: setCentralActivePage,
    getAllCitizens
  } = useCitizen();

  const [activePage, setActivePage] = useState('home');

  useEffect(() => {
    // استخدام المواطن المختار من النظام المركزي
    if (centralCitizen) {
      setCentralActivePage('portal');
    }
  }, [centralCitizen, setCentralActivePage]);

  // تسجيل التفاعل منفصل لتجنب الحلقة اللا نهائية
  // useEffect(() => {
  //   if (centralCitizen && activePage) {
  //     addInteraction({
  //       type: 'portal_visit',
  //       page: activePage,
  //       citizenId: centralCitizen.id
  //     });
  //   }
  // }, [centralCitizen, activePage, addInteraction]); // مُعطل مؤقتاً لحل الحلقة اللا نهائية

  // إنشاء إشعارات استباقية مخصصة للمواطن
  const getPersonalizedNotifications = (citizen) => {
    if (!citizen) return [];
    
    const notifications = [];
    const firstName = citizen.name.split(' ')[0];
    
    // إشعارات استباقية حسب نوع الحالة
    if (citizen.profile === 'domestic_violence_victim') {
      // تنبيهات عاجلة للعنف الأسري
      notifications.push({
        id: `emergency-${citizen.id}`,
        type: 'critical',
        icon: '🚨',
        title: 'تنبيه أمني عاجل',
        message: `${firstName}، تم رصد نشاط مشبوه في منطقتك. فريق الحماية تم تنبيهه تلقائياً وهو في طريقه إليك.`,
        timestamp: new Date(Date.now() - 15 * 60 * 1000), // منذ 15 دقيقة
        read: false,
        action: 'اتصال طوارئ',
        priority: 'critical'
      });
      
      notifications.push({
        id: `safety-plan-${citizen.id}`,
        type: 'warning',
        icon: '🛡️',
        title: 'تحديث خطة الأمان',
        message: `${firstName}، تم تحديث خطة الأمان الخاصة بك بناءً على التطورات الأخيرة. يرجى مراجعتها فوراً.`,
        timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000), // منذ ساعتين
        read: false,
        action: 'مراجعة الخطة',
        priority: 'high'
      });
      
      notifications.push({
        id: `counseling-${citizen.id}`,
        type: 'support',
        icon: '💚',
        title: 'جلسة دعم نفسي',
        message: `${firstName}، د. منى السعد متاحة الآن لجلسة دعم نفسي عاجلة. تم حجز موعد لك تلقائياً.`,
        timestamp: new Date(Date.now() - 45 * 60 * 1000), // منذ 45 دقيقة
        read: false,
        action: 'انضمام للجلسة',
        priority: 'high'
      });
    }
    
    else if (citizen.profile === 'license_issue') {
      // تنبيهات استباقية للرخص
      notifications.push({
        id: `license-expiry-${citizen.id}`,
        type: 'warning',
        icon: '⏰',
        title: 'تنبيه انتهاء الرخصة',
        message: `${firstName}، رخصة العمل الخاصة بك ستنتهي خلال 15 يوم. تم إعداد طلب التجديد تلقائياً.`,
        timestamp: new Date(Date.now() - 30 * 60 * 1000), // منذ 30 دقيقة
        read: false,
        action: 'مراجعة الطلب',
        priority: 'medium'
      });
      
      notifications.push({
        id: `documents-ready-${citizen.id}`,
        type: 'success',
        icon: '📄',
        title: 'الوثائق جاهزة',
        message: `${firstName}، تم تجهيز جميع الوثائق المطلوبة لتجديد رخصتك. يمكنك الاستلام من أقرب مركز.`,
        timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000), // منذ 4 ساعات
        read: false,
        action: 'عرض المواقع',
        priority: 'medium'
      });
      
      notifications.push({
        id: `fast-track-${citizen.id}`,
        type: 'info',
        icon: '⚡',
        title: 'خدمة سريعة متاحة',
        message: `${firstName}، بناءً على ملفك الممتاز، تم تفعيل الخدمة السريعة لك. التجديد خلال ساعة واحدة فقط.`,
        timestamp: new Date(Date.now() - 6 * 60 * 60 * 1000), // منذ 6 ساعات
        read: true,
        action: 'تفعيل الخدمة',
        priority: 'low'
      });
    }
    
    else if (citizen.profile === 'password_reset') {
      // تنبيهات استباقية لكلمة المرور
      notifications.push({
        id: `security-alert-${citizen.id}`,
        type: 'warning',
        icon: '🔐',
        title: 'تنبيه أمني',
        message: `${firstName}، تم رصد محاولات دخول مشبوهة لحسابك. تم تأمين الحساب تلقائياً وإرسال رمز التحقق.`,
        timestamp: new Date(Date.now() - 20 * 60 * 1000), // منذ 20 دقيقة
        read: false,
        action: 'تأمين الحساب',
        priority: 'high'
      });
      
      notifications.push({
        id: `password-guide-${citizen.id}`,
        type: 'info',
        icon: '📱',
        title: 'دليل إعادة التعيين',
        message: `${firstName}، تم إرسال دليل مبسط لإعادة تعيين كلمة المرور عبر الواتساب. خطوات سهلة وآمنة.`,
        timestamp: new Date(Date.now() - 1 * 60 * 60 * 1000), // منذ ساعة
        read: false,
        action: 'فتح الدليل',
        priority: 'medium'
      });
      
      notifications.push({
        id: `backup-access-${citizen.id}`,
        type: 'success',
        icon: '🔑',
        title: 'طريقة دخول بديلة',
        message: `${firstName}، تم تفعيل طريقة دخول بديلة عبر بصمة الوجه. يمكنك الدخول الآن بدون كلمة مرور.`,
        timestamp: new Date(Date.now() - 3 * 60 * 60 * 1000), // منذ 3 ساعات
        read: true,
        action: 'تجربة الدخول',
        priority: 'low'
      });
    }
    
    return notifications;
  };

  // إنشاء خدمات استباقية مخصصة للمواطن
  const getPersonalizedServices = (citizen) => {
    if (!citizen) return [];
    
    const services = [];
    const firstName = citizen.name.split(' ')[0];
    
    // خدمات استباقية حسب نوع الحالة
    if (citizen.profile === 'domestic_violence_victim') {
      // خدمات حماية عاجلة
      services.push({
        id: 'emergency-protection',
        title: '🚨 حماية فورية نشطة',
        description: `${firstName}، فريق الحماية متاح 24/7. تم تفعيل نظام التتبع الآمن تلقائياً.`,
        status: 'نشط الآن',
        color: 'red',
        urgent: true,
        autoActivated: true
      });
      
      services.push({
        id: 'safe-house',
        title: '🏠 بيت آمن مُجهز',
        description: `${firstName}، تم حجز مكان آمن لك في منطقة محمية. جاهز للانتقال فوراً.`,
        status: 'محجوز',
        color: 'orange',
        urgent: true,
        autoActivated: true
      });
      
      services.push({
        id: 'legal-support',
        title: '⚖️ دعم قانوني متخصص',
        description: `${firstName}، المحامية سارة أحمد متاحة لك مجاناً. تم إعداد ملفك القانوني مسبقاً.`,
        status: 'جاهز',
        color: 'blue',
        autoActivated: true
      });
      
      services.push({
        id: 'psychological-support',
        title: '💚 دعم نفسي مكثف',
        description: `${firstName}، جلسات دعم نفسي يومية مع د. منى السعد. البرنامج مُصمم خصيصاً لحالتك.`,
        status: 'مُفعل',
        color: 'green',
        autoActivated: true
      });
    }
    
    else if (citizen.profile === 'license_issue') {
      // خدمات الرخص المُحسنة
      services.push({
        id: 'fast-renewal',
        title: '⚡ تجديد سريع مُفعل',
        description: `${firstName}، بناءً على سجلك الممتاز، تم تفعيل الخدمة السريعة. التجديد خلال ساعة واحدة.`,
        status: 'مُفعل تلقائياً',
        color: 'green',
        autoActivated: true
      });
      
      services.push({
        id: 'document-preparation',
        title: '📄 إعداد الوثائق تلقائياً',
        description: `${firstName}، تم إعداد جميع الوثائق المطلوبة مسبقاً. لا حاجة لزيارات متعددة.`,
        status: 'جاهز للاستلام',
        color: 'blue',
        autoActivated: true
      });
      
      services.push({
        id: 'mobile-service',
        title: '🚗 خدمة متنقلة',
        description: `${firstName}، يمكن لفريقنا المتنقل زيارتك في مكان عملك لإنهاء الإجراءات.`,
        status: 'متاح',
        color: 'orange'
      });
      
      services.push({
        id: 'digital-license',
        title: '📱 رخصة رقمية',
        description: `${firstName}، تم إعداد رخصتك الرقمية على أبشر. صالحة قانونياً ومتاحة فوراً.`,
        status: 'جاهز',
        color: 'green'
      });
    }
    
    else if (citizen.profile === 'password_reset') {
      // خدمات تقنية مُبسطة
      services.push({
        id: 'guided-reset',
        title: '🎯 إعادة تعيين مُوجهة',
        description: `${firstName}، مساعد ذكي سيرشدك خطوة بخطوة عبر مكالمة فيديو مباشرة.`,
        status: 'متاح الآن',
        color: 'blue',
        urgent: true
      });
      
      services.push({
        id: 'biometric-setup',
        title: '👤 تفعيل بصمة الوجه',
        description: `${firstName}، تم إعداد نظام بصمة الوجه لك. دخول آمن بدون كلمة مرور.`,
        status: 'جاهز للتفعيل',
        color: 'green',
        autoActivated: true
      });
      
      services.push({
        id: 'security-upgrade',
        title: '🔐 ترقية الأمان',
        description: `${firstName}، تم تفعيل التحقق الثنائي وحماية إضافية لحسابك تلقائياً.`,
        status: 'مُفعل',
        color: 'orange',
        autoActivated: true
      });
      
      services.push({
        id: 'tech-support',
        title: '🛠️ دعم تقني مخصص',
        description: `${firstName}، فني متخصص متاح لمساعدتك عن بُعد أو زيارة منزلية مجانية.`,
        status: 'متاح',
        color: 'blue'
      });
    }
    
    return services;
  };

  // إنشاء اقتراحات استباقية ذكية للمواطن
  const getPersonalizedSuggestions = (citizen) => {
    if (!citizen) return [];
    
    const suggestions = [];
    const firstName = citizen.name.split(' ')[0];
    
    // اقتراحات استباقية حسب نوع الحالة
    if (citizen.profile === 'domestic_violence_victim') {
      // اقتراحات حماية استباقية
      suggestions.push({
        id: 'safety-network',
        title: '🤝 شبكة أمان اجتماعية',
        description: `${firstName}، نقترح بناء شبكة دعم من 5 أشخاص موثوقين. سيتم تدريبهم على كيفية مساعدتك.`,
        priority: 'عاجل',
        color: 'red',
        aiGenerated: true,
        estimatedImpact: 'عالي'
      });
      
      suggestions.push({
        id: 'relocation-plan',
        title: '🏠 خطة انتقال آمنة',
        description: `${firstName}، بناءً على تحليل المخاطر، نقترح الانتقال لمنطقة أكثر أماناً. تم إعداد 3 خيارات مناسبة.`,
        priority: 'عاجل',
        color: 'orange',
        aiGenerated: true,
        estimatedImpact: 'عالي جداً'
      });
      
      suggestions.push({
        id: 'legal-preparation',
        title: '⚖️ إعداد قانوني استباقي',
        description: `${firstName}، نقترح إعداد الوثائق القانونية مسبقاً (أمر حماية، توكيل). سيوفر عليك وقت ثمين.`,
        priority: 'مهم',
        color: 'blue',
        aiGenerated: true,
        estimatedImpact: 'عالي'
      });
      
      suggestions.push({
        id: 'skill-building',
        title: '💪 برنامج بناء المهارات',
        description: `${firstName}، نقترح برنامج تدريبي لبناء الثقة والمهارات المهنية. سيساعدك على الاستقلالية.`,
        priority: 'مفيد',
        color: 'green',
        aiGenerated: true,
        estimatedImpact: 'متوسط'
      });
    }
    
    else if (citizen.profile === 'license_issue') {
      // اقتراحات تحسين الخدمات
      suggestions.push({
        id: 'career-advancement',
        title: '📈 تطوير مهني',
        description: `${firstName}، بناءً على مجال عملك، نقترح دورات تدريبية مجانية لتطوير مهاراتك وزيادة راتبك.`,
        priority: 'مهم',
        color: 'blue',
        aiGenerated: true,
        estimatedImpact: 'عالي'
      });
      
      suggestions.push({
        id: 'business-license',
        title: '🏢 رخصة عمل حر',
        description: `${firstName}، ملفك يؤهلك للحصول على رخصة عمل حر. يمكن أن تزيد دخلك بنسبة 40%.`,
        priority: 'مهم',
        color: 'green',
        aiGenerated: true,
        estimatedImpact: 'عالي'
      });
      
      suggestions.push({
        id: 'digital-services',
        title: '📱 خدمات رقمية متقدمة',
        description: `${firstName}، نقترح تفعيل جميع الخدمات الرقمية لتوفير الوقت والجهد في المستقبل.`,
        priority: 'مفيد',
        color: 'blue',
        aiGenerated: true,
        estimatedImpact: 'متوسط'
      });
      
      suggestions.push({
        id: 'insurance-upgrade',
        title: '🛡️ ترقية التأمين',
        description: `${firstName}، بناءً على وضعك المهني، نقترح ترقية تأمينك الصحي والمهني بخصم 25%.`,
        priority: 'مفيد',
        color: 'orange',
        aiGenerated: true,
        estimatedImpact: 'متوسط'
      });
    }
    
    else if (citizen.profile === 'password_reset') {
      // اقتراحات تقنية وأمنية
      suggestions.push({
        id: 'digital-literacy',
        title: '💻 محو الأمية الرقمية',
        description: `${firstName}، نقترح دورة مجانية لتعلم أساسيات الأمان الرقمي. ستحميك من المشاكل المستقبلية.`,
        priority: 'مهم',
        color: 'blue',
        aiGenerated: true,
        estimatedImpact: 'عالي'
      });
      
      suggestions.push({
        id: 'smart-devices',
        title: '📱 أجهزة ذكية مساعدة',
        description: `${firstName}، نقترح استخدام أجهزة ذكية مبسطة تسهل عليك الوصول للخدمات الحكومية.`,
        priority: 'مفيد',
        color: 'green',
        aiGenerated: true,
        estimatedImpact: 'متوسط'
      });
      
      suggestions.push({
        id: 'family-support',
        title: '👨‍👩‍👧‍👦 دعم عائلي تقني',
        description: `${firstName}، نقترح تدريب أحد أفراد عائلتك ليكون مساعدك التقني الشخصي.`,
        priority: 'مفيد',
        color: 'orange',
        aiGenerated: true,
        estimatedImpact: 'عالي'
      });
      
      suggestions.push({
        id: 'simplified-interface',
        title: '🎯 واجهة مبسطة',
        description: `${firstName}، نقترح تفعيل الواجهة المبسطة في جميع التطبيقات الحكومية لسهولة الاستخدام.`,
        priority: 'مفيد',
        color: 'blue',
        aiGenerated: true,
        estimatedImpact: 'متوسط'
      });
    }
    
    return suggestions;
  };


  // حساب الأعداد المخصصة
  const personalNotifications = getPersonalizedNotifications(centralCitizen);
  const personalServices = getPersonalizedServices(centralCitizen);
  const personalSuggestions = getPersonalizedSuggestions(centralCitizen);
  
  const unreadNotificationsCount = personalNotifications.filter(n => !n.read).length;
  const urgentServicesCount = personalServices.filter(s => s.urgent).length;
  const prioritySuggestionsCount = personalSuggestions.filter(s => s.priority === 'عاجل').length;

  // صفحات النظام
  const pages = [
    { id: 'home', name: 'الرئيسية', icon: Home, color: 'blue' },
    { id: 'chat', name: 'شات سبق', icon: MessageSquare, color: 'cyan' },
    { id: 'notifications', name: 'الإشعارات', icon: Bell, color: 'red', badge: unreadNotificationsCount },
    { id: 'services', name: 'خدماتي', icon: Shield, color: 'green', badge: urgentServicesCount },
    { id: 'suggestions', name: 'اقتراحات', icon: Lightbulb, color: 'yellow', badge: prioritySuggestionsCount },
    { id: 'support', name: 'الدعم', icon: Heart, color: 'pink' }
  ];

  // قائمة المواطنين الحقيقية من النظام المركزي
  const allCitizens = getAllCitizens();
  const citizenOptions = allCitizens.map(citizen => ({
    id: citizen.id,
    name: citizen.name,
    profile: citizen.profile,
    issueType: citizen.issueType,
    risk: citizen.riskLevel,
    region: citizen.region,
    city: citizen.city,
    complaints: citizen.complaints,
    description: citizen.description
  }));

  // صفحة الرئيسية
  const HomePage = () => (
    <div className="space-y-6">

      {/* ترحيب مخصص */}
      {centralCitizen && (
        <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-2xl shadow-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                <User className="w-8 h-8" />
              </div>
              <div>
                <h2 className="text-2xl font-bold mb-1">
                  مرحباً، {centralCitizen.name.split(' ')[0]}
                </h2>
                <div className="flex items-center gap-4 text-sm opacity-90">
                  <div className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    {centralCitizen.city}، {centralCitizen.region}
                  </div>
                  <div className="flex items-center gap-1">
                    <Shield className="w-4 h-4" />
                    {centralCitizen.riskLevel > 70 ? 'تحت الحماية النشطة' : 'متابعة عادية'}
                  </div>
                </div>
              </div>
            </div>
            <div className="text-center">
              <div className={`rounded-full w-12 h-12 flex items-center justify-center mb-1 ${
                centralCitizen.riskLevel > 70 ? 'bg-red-500 animate-pulse' : 'bg-blue-500'
              }`}>
                <Bell className="w-6 h-6" />
              </div>
              <p className="text-sm font-bold">{centralCitizen.complaints} شكوى نشطة</p>
              <p className="text-xs opacity-75">مستوى الخطر: {centralCitizen.riskLevel}/100</p>
            </div>
          </div>
        </div>
      )}

      {/* ملخص سريع */}
      <div className="bg-white rounded-xl shadow-lg p-4">
        <h3 className="font-bold text-gray-800 mb-3">ملخص اليوم</h3>
        <div className="grid md:grid-cols-3 gap-4">
          <div className="text-center p-3 bg-green-50 rounded-lg">
            <Shield className="w-6 h-6 text-green-600 mx-auto mb-2" />
            <p className="text-sm font-bold text-green-800">خطة الأمان</p>
            <p className="text-xs text-gray-600">نشطة ومحدثة</p>
          </div>
          <div className="text-center p-3 bg-blue-50 rounded-lg">
            <Phone className="w-6 h-6 text-blue-600 mx-auto mb-2" />
            <p className="text-sm font-bold text-blue-800">المتابعة</p>
            <p className="text-xs text-gray-600">يومية - د. منى</p>
          </div>
          <div className="text-center p-3 bg-purple-50 rounded-lg">
            <Clock className="w-6 h-6 text-purple-600 mx-auto mb-2" />
            <p className="text-sm font-bold text-purple-800">التقدم</p>
            <p className="text-xs text-gray-600">75% مكتمل</p>
          </div>
        </div>
      </div>

      {/* أزرار الطوارئ */}
      <div className="bg-gradient-to-r from-red-500 to-red-600 rounded-xl p-4 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" />
            <span className="font-bold">مساعدة عاجلة؟</span>
          </div>
          <div className="flex gap-2">
            <button className="bg-white text-red-600 px-4 py-2 rounded-lg font-bold text-sm hover:bg-gray-100 transition">
              📞 1919
            </button>
            <button className="bg-white bg-opacity-20 text-white px-4 py-2 rounded-lg font-bold text-sm hover:bg-opacity-30 transition">
              💬 محادثة
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // صفحة الإشعارات
  const NotificationsPage = () => {
    const personalNotifications = getPersonalizedNotifications(centralCitizen);
    
    return (
      <div className="space-y-4">
        <h2 className="text-2xl font-bold text-gray-800 mb-6">
          إشعارات {centralCitizen?.name.split(' ')[0] || 'المواطن'}
        </h2>
        
        {personalNotifications.length === 0 ? (
          <div className="text-center py-8">
            <Bell className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">لا توجد إشعارات جديدة</p>
          </div>
        ) : (
          personalNotifications.map((notif) => (
        <div key={notif.id} className="bg-white rounded-xl shadow-lg p-4 border-l-4 border-red-500">
          <div className="flex items-start gap-3">
            <div className="text-2xl">{notif.icon}</div>
            <div className="flex-1">
              <div className="flex items-start justify-between mb-2">
                <h3 className="font-bold text-lg text-gray-800">
                  {notif.title}
                  {!notif.read && (
                    <span className="ml-2 bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                      جديد
                    </span>
                  )}
                </h3>
                <span className="text-xs text-gray-500">
                  {new Date(notif.timestamp).toLocaleString('ar-SA', {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </span>
              </div>
              <p className="text-gray-700 mb-3 leading-relaxed">{notif.message}</p>
              <button 
                onClick={() => console.log('تم النقر على:', notif.action)}
                className={`px-4 py-2 rounded-lg font-medium transition ${
                  notif.priority === 'critical' ? 'bg-red-600 hover:bg-red-700 text-white' :
                  notif.priority === 'high' ? 'bg-orange-600 hover:bg-orange-700 text-white' :
                  'bg-blue-600 hover:bg-blue-700 text-white'
                }`}
              >
                {notif.action}
              </button>
            </div>
          </div>
        </div>
          ))
        )}
      </div>
    );
  };

  // صفحة الخدمات
  const ServicesPage = () => {
    const personalServices = getPersonalizedServices(centralCitizen);
    
    return (
      <div className="space-y-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-6">
          خدمات {centralCitizen?.name.split(' ')[0] || 'المواطن'}
        </h2>
        
        <div className="grid gap-4">
          {personalServices.map((service) => (
            <div key={service.id} className={`border rounded-xl p-6 ${
              service.color === 'red' ? 'bg-red-50 border-red-200' :
              service.color === 'orange' ? 'bg-orange-50 border-orange-200' :
              service.color === 'blue' ? 'bg-blue-50 border-blue-200' :
              'bg-green-50 border-green-200'
            }`}>
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className={`font-bold text-lg ${
                      service.color === 'red' ? 'text-red-800' :
                      service.color === 'orange' ? 'text-orange-800' :
                      service.color === 'blue' ? 'text-blue-800' :
                      'text-green-800'
                    }`}>
                      {service.title}
                    </h3>
                    {service.autoActivated && (
                      <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full font-bold animate-pulse">
                        🤖 تلقائي
                      </span>
                    )}
                  </div>
                  <p className="text-gray-600 text-sm leading-relaxed">{service.description}</p>
                </div>
                <div className="text-right ml-4">
                  <p className={`text-sm font-bold mb-1 ${
                    service.color === 'red' ? 'text-red-600' :
                    service.color === 'orange' ? 'text-orange-600' :
                    service.color === 'blue' ? 'text-blue-600' :
                    'text-green-600'
                  }`}>
                    {service.status}
                  </p>
                  {service.urgent && (
                    <p className="text-xs text-red-500 font-bold bg-red-100 px-2 py-1 rounded animate-pulse">
                      🚨 عاجل
                    </p>
                  )}
                  {service.autoActivated && (
                    <p className="text-xs text-green-600 font-bold">
                      ✓ مُفعل تلقائياً
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // صفحة الاقتراحات
  const SuggestionsPage = () => {
    const personalSuggestions = getPersonalizedSuggestions(centralCitizen);
    
    return (
      <div className="space-y-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-6">
          اقتراحات لـ {centralCitizen?.name.split(' ')[0] || 'المواطن'}
        </h2>
        
        <div className="grid gap-4">
          {personalSuggestions.map((suggestion) => (
            <div key={suggestion.id} className={`bg-white rounded-xl shadow-lg p-6 border-l-4 ${
              suggestion.color === 'red' ? 'border-red-500' :
              suggestion.color === 'orange' ? 'border-orange-500' :
              suggestion.color === 'blue' ? 'border-blue-500' :
              'border-green-500'
            }`}>
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="font-bold text-lg text-gray-800">
                      {suggestion.title}
                    </h3>
                    {suggestion.aiGenerated && (
                      <span className="bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded-full font-bold">
                        🧠 ذكي
                      </span>
                    )}
                  </div>
                  <p className="text-gray-600 mb-2 leading-relaxed">{suggestion.description}</p>
                  {suggestion.estimatedImpact && (
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-gray-500">التأثير المتوقع:</span>
                      <span className={`font-bold ${
                        suggestion.estimatedImpact === 'عالي جداً' ? 'text-red-600' :
                        suggestion.estimatedImpact === 'عالي' ? 'text-orange-600' :
                        'text-green-600'
                      }`}>
                        {suggestion.estimatedImpact}
                      </span>
                    </div>
                  )}
                </div>
                <div className="text-right ml-4">
                  <span className={`px-3 py-1 rounded-full text-sm font-bold ${
                    suggestion.priority === 'عاجل' ? 'bg-red-100 text-red-800' :
                    suggestion.priority === 'مهم' ? 'bg-orange-100 text-orange-800' :
                    'bg-green-100 text-green-800'
                  }`}>
                    {suggestion.priority}
                  </span>
                </div>
              </div>
              <div className="flex gap-2">
                <button className={`px-4 py-2 rounded-lg font-medium transition ${
                  suggestion.color === 'red' ? 'bg-red-600 hover:bg-red-700 text-white' :
                  suggestion.color === 'orange' ? 'bg-orange-600 hover:bg-orange-700 text-white' :
                  suggestion.color === 'blue' ? 'bg-blue-600 hover:bg-blue-700 text-white' :
                  'bg-green-600 hover:bg-green-700 text-white'
                }`}>
                  تفعيل الآن
                </button>
                <button className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition">
                  تفاصيل أكثر
                </button>
                {suggestion.aiGenerated && (
                  <button className="px-3 py-2 bg-purple-100 text-purple-700 rounded-lg font-medium hover:bg-purple-200 transition text-sm">
                    🤖 كيف تم الاقتراح؟
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // صفحة الدعم
  const SupportPage = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-800 mb-6">الدعم والمساعدة</h2>
      
      <div className="bg-gradient-to-r from-red-500 to-red-600 rounded-xl p-6 text-white">
        <h3 className="text-xl font-bold mb-4">🚨 خطوط الطوارئ</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <button className="bg-white text-red-600 py-4 rounded-xl font-bold text-lg hover:bg-gray-100 transition">
            📞 خط الحماية - 1919
          </button>
          <button className="bg-white bg-opacity-20 text-white py-4 rounded-xl font-bold text-lg hover:bg-opacity-30 transition">
            💬 محادثة سرية فورية
          </button>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="font-bold text-lg text-gray-800 mb-4">📞 أرقام مهمة</h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
              <span>خط الحماية الوطني</span>
              <span className="font-bold text-blue-600">1919</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
              <span>الشرطة</span>
              <span className="font-bold text-blue-600">999</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
              <span>الإسعاف</span>
              <span className="font-bold text-blue-600">997</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="font-bold text-lg text-gray-800 mb-4">📍 أماكن آمنة</h3>
          <div className="space-y-3">
            <div className="p-3 bg-green-50 rounded-lg">
              <p className="font-bold text-green-800">مركز الحماية</p>
              <p className="text-sm text-gray-600">شارع الملك فهد - 2 كم</p>
            </div>
            <div className="p-3 bg-blue-50 rounded-lg">
              <p className="font-bold text-blue-800">بيت أختي - نورا</p>
              <p className="text-sm text-gray-600">الملز - 5 كم</p>
            </div>
            <div className="p-3 bg-purple-50 rounded-lg">
              <p className="font-bold text-purple-800">مسجد الحي</p>
              <p className="text-sm text-gray-600">الملز - 500 م</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // صفحة الشات
  const ChatPage = () => {
    console.log('🔄 تم تحميل ChatPage مع المواطن:', centralCitizen?.name);
    return (
      <div className="space-y-6">
        <SmartChatAgent citizen={centralCitizen} />
      </div>
    );
  };

  // عرض الصفحة النشطة
  const renderActivePage = () => {
    switch(activePage) {
      case 'home': return <HomePage />;
      case 'chat': return <ChatPage />;
      case 'notifications': return <NotificationsPage />;
      case 'services': return <ServicesPage />;
      case 'suggestions': return <SuggestionsPage />;
      case 'support': return <SupportPage />;
      default: return <HomePage />;
    }
  };

  return (
    <div className="space-y-6">
      {/* اختيار المواطن - 3 حالات أساسية */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl shadow-lg border border-blue-100 p-3">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
            <h3 className="text-sm font-bold text-gray-800">
              اختر حالة للاختبار
            </h3>
          </div>
          {centralCitizen && (
            <button
              onClick={() => {
                console.log('مسح سريع...');
                clearSelection();
              }}
              className="bg-red-500 text-white px-2 py-1 rounded-full text-xs font-medium hover:bg-red-600 transition-all transform hover:scale-105 shadow-sm"
            >
              ✕
            </button>
          )}
        </div>
        
        <div className="grid grid-cols-3 gap-2">
          {citizenOptions.map((option) => (
            <button
              key={option.id}
              onClick={() => {
                console.log('تم النقر على المواطن:', option.name, 'ID:', option.id);
                console.log('المواطن الحالي:', centralCitizen?.name, 'ID:', centralCitizen?.id);
                
                // مسح قوي أولاً ثم اختيار جديد
                if (centralCitizen?.id === option.id) {
                  console.log('إلغاء اختيار نفس المواطن');
                  clearSelection();
                } else {
                  console.log('اختيار مواطن جديد');
                  // مسح الاختيار السابق أولاً
                  if (centralCitizen) {
                    clearSelection();
                    // انتظار قصير ثم اختيار الجديد
                    setTimeout(() => {
                      selectCitizen(option.id);
                    }, 100);
                  } else {
                    selectCitizen(option.id);
                  }
                }
              }}
              className={`p-2 rounded-lg transition-all duration-200 transform hover:scale-105 ${
                centralCitizen?.id === option.id 
                  ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg border-2 border-blue-300' 
                  : 'bg-white hover:bg-gray-50 border border-gray-200 hover:border-blue-300 hover:shadow-md'
              }`}
            >
              <div className="flex items-center justify-between">
                {/* الاسم على اليمين */}
                <div className="text-right flex-1">
                  <h4 className={`font-bold text-xs leading-tight ${
                    centralCitizen?.id === option.id ? 'text-white' : 'text-gray-800'
                  }`}>
                    {option.name}
                  </h4>
                  <p className={`text-xs leading-tight ${
                    centralCitizen?.id === option.id ? 'text-blue-100' : 'text-gray-500'
                  }`}>
                    {option.region}
                  </p>
                  {centralCitizen?.id === option.id && (
                    <div className="text-yellow-300 font-bold text-xs animate-pulse">✓ مختار</div>
                  )}
                </div>
                
                {/* الحالة على اليسار */}
                <div className="flex flex-col items-center">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-sm shadow-sm ${
                    centralCitizen?.id === option.id 
                      ? 'bg-white bg-opacity-20 text-white' 
                      : option.profile === 'domestic_violence_victim' ? 'bg-gradient-to-r from-red-400 to-red-500 text-white' :
                        option.profile === 'license_issue' ? 'bg-gradient-to-r from-blue-400 to-blue-500 text-white' :
                        'bg-gradient-to-r from-green-400 to-green-500 text-white'
                  }`}>
                    {option.profile === 'domestic_violence_victim' ? '🚨' :
                     option.profile === 'license_issue' ? '📄' : '🔑'}
                  </div>
                  <div className={`px-1.5 py-0.5 rounded-full text-xs font-bold mt-1 shadow-sm ${
                    centralCitizen?.id === option.id 
                      ? 'bg-white bg-opacity-20 text-white' 
                      : option.profile === 'domestic_violence_victim' ? 'bg-gradient-to-r from-red-100 to-red-200 text-red-800' :
                        option.profile === 'license_issue' ? 'bg-gradient-to-r from-blue-100 to-blue-200 text-blue-800' :
                        'bg-gradient-to-r from-green-100 to-green-200 text-green-800'
                  }`}>
                    {option.issueType}
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* شريط التنقل */}
      <div className="bg-white rounded-xl shadow-lg p-4">
        <div className="flex flex-wrap gap-2">
          {pages.map((page) => {
            const Icon = page.icon;
            const isActive = activePage === page.id;
            return (
              <button
                key={page.id}
                onClick={() => setActivePage(page.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition relative ${
                  isActive 
                    ? `bg-${page.color}-100 text-${page.color}-800 border-2 border-${page.color}-300` 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span className="text-sm">{page.name}</span>
                {page.badge > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                    {page.badge}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* محتوى الصفحة النشطة */}
      <div className="min-h-96">
        {renderActivePage()}
      </div>
    </div>
  );
};

export default CitizenPortalPages;
